import {
  BarChart3,
  Bot,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  CircleHelp,
  Globe,
  LayoutGrid,
  Layers,
  LayoutDashboard,
  LineChart,
  Loader2,
  LogOut,
  Megaphone,
  Moon,
  Package,
  PenSquare,
  PlugZap,
  Plus,
  Send,
  Settings,
  Sun,
  Users,
} from 'lucide-react';
import * as React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useTheme } from '../providers/ThemeProvider';
import { api } from '../lib/api-client';
import { decodeJwtPayload } from '../lib/auth';
import { useAuthStore, type BrandSummary } from '../stores/auth-store';
import { Tooltip } from '../components/ui/tooltip';
import { cn } from '../lib/utils';

// ── Nav structure ─────────────────────────────────────────────────

interface NavChild {
  id: string;
  to: string;
  label: string;
  pill?: number;
}

interface NavItem {
  id: string;
  to: string;
  label: string;
  icon: React.ElementType;
  badge?: string;
  count?: string;
  children?: NavChild[];
}

interface NavGroup {
  group: string;
  icon: React.ElementType;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    group: 'Workspace',
    icon: LayoutGrid,
    items: [
      { id: 'dashboard', to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    ],
  },
  {
    group: 'Audience',
    icon: Users,
    items: [
      { id: 'contacts',  to: '/contacts',  label: 'Contacts',  icon: Users },
      { id: 'segments',  to: '/segments',  label: 'Segments',  icon: Users },
      { id: 'products',  to: '/products',  label: 'Products',  icon: Package },
    ],
  },
  {
    group: 'Compose',
    icon: PenSquare,
    items: [
      { id: 'campaigns',   to: '/campaigns',   label: 'Campaigns',   icon: Megaphone },
      { id: 'automations', to: '/automations', label: 'Automations', icon: Bot },
      { id: 'templates',   to: '/templates',   label: 'Templates',   icon: Send },
      { id: 'popups',      to: '/popups',      label: 'Popups',      icon: Layers },
    ],
  },
  {
    group: 'Insights',
    icon: LineChart,
    items: [
      { id: 'analytics', to: '/analytics', label: 'Analytics', icon: BarChart3 },
    ],
  },
  {
    group: 'Integrations',
    icon: PlugZap,
    items: [
      { id: 'connectors', to: '/connectors', label: 'Connectors', icon: PlugZap },
    ],
  },
];

// ── BrandMark ─────────────────────────────────────────────────────

function BrandMark({ size = 28 }: { size?: number }) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: 8,
        background: 'linear-gradient(135deg, oklch(62% 0.20 270), oklch(42% 0.23 270))',
        position: 'relative',
        flexShrink: 0,
        boxShadow: '0 4px 10px -2px oklch(44% 0.23 270 / 0.40)',
      }}
    >
      <span
        style={{
          position: 'absolute',
          inset: Math.round(size * 0.21),
          borderRadius: 3,
          background: 'rgba(255,255,255,0.92)',
          clipPath: 'polygon(0 0, 55% 0, 100% 45%, 100% 100%, 45% 100%, 0 55%)',
        }}
      />
    </div>
  );
}

// ── Workspace switcher ────────────────────────────────────────────

function planLabel(planId: string | null | undefined): string {
  if (!planId) return 'Free plan';
  const map: Record<string, string> = { free: 'Free plan', growth: 'Growth plan', pro: 'Pro plan', scale: 'Scale plan', enterprise: 'Enterprise' };
  return map[planId.toLowerCase()] ?? `${planId.charAt(0).toUpperCase()}${planId.slice(1)} plan`;
}

function WebsiteMark({ brand, size = 22 }: { brand: BrandSummary; size?: number }) {
  if (brand.logoUrl) {
    return (
      <img
        src={brand.logoUrl}
        alt=""
        width={size}
        height={size}
        style={{ borderRadius: 6, objectFit: 'cover', flexShrink: 0 }}
      />
    );
  }
  return (
    <div
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        borderRadius: 6,
        background: 'var(--brand-muted)',
        color: 'var(--brand-hover)',
        display: 'grid',
        placeItems: 'center',
        fontSize: size * 0.42,
        fontWeight: 700,
        textTransform: 'uppercase',
        flexShrink: 0,
      }}
    >
      {brand.name.slice(0, 1)}
    </div>
  );
}

function WorkspaceSwitcher({ expanded }: { expanded: boolean }) {
  const navigate = useNavigate();
  const orgId = useAuthStore((s) => s.orgId);
  const brands = useAuthStore((s) => s.brands);
  const setBrands = useAuthStore((s) => s.setBrands);
  const setAuth = useAuthStore((s) => s.setAuth);
  const user = useAuthStore((s) => s.user);
  const role = useAuthStore((s) => s.role);

  const [accountName, setAccountName] = React.useState<string | null>(null);
  const [open, setOpen] = React.useState(false);
  const [switchingId, setSwitchingId] = React.useState<string | null>(null);
  const [adding, setAdding] = React.useState(false);
  const [creating, setCreating] = React.useState(false);
  const [newName, setNewName] = React.useState('');
  const menuRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);

  const currentBrand = brands.find((b) => b.id === orgId) ?? null;

  // Load the organization (umbrella account) name + its websites. When the
  // account layer isn't provisioned, fall back to the active organization so
  // the switcher still shows real data and the current plan.
  React.useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    api
      .get<{ name: string; brands: BrandSummary[] }>('/account')
      .then((res) => {
        if (cancelled) return;
        setAccountName(res.data.name);
        if (Array.isArray(res.data.brands) && res.data.brands.length > 0) setBrands(res.data.brands);
      })
      .catch(() => {
        api
          .get<{ id: string; name: string; slug: string; planId: string | null; settings?: { logoUrl: string | null } }>(
            `/organizations/${orgId}`,
          )
          .then((r) => {
            if (cancelled) return;
            setAccountName(r.data.name);
            setBrands([
              {
                id: r.data.id,
                name: r.data.name,
                slug: r.data.slug,
                planId: r.data.planId ?? null,
                logoUrl: r.data.settings?.logoUrl ?? null,
                role: null,
              },
            ]);
          })
          .catch(() => {/* non-fatal — switcher shows fallbacks */});
      });
    return () => { cancelled = true; };
  }, [orgId, setBrands]);

  // Close on outside click / Escape
  React.useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent) {
      if (!menuRef.current?.contains(e.target as Node) && !triggerRef.current?.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') { setOpen(false); triggerRef.current?.focus(); }
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  async function switchTo(brand: BrandSummary) {
    if (brand.id === orgId) { setOpen(false); return; }
    setSwitchingId(brand.id);
    try {
      const res = await api.post<{ accessToken: string; refreshToken: string; brandId: string }>(
        `/auth/brands/${brand.id}/switch`,
      );
      const decoded = decodeJwtPayload(res.data.accessToken);
      setAuth({
        user: user!,
        token: res.data.accessToken,
        refreshToken: res.data.refreshToken,
        accountId: decoded?.accountId ?? null,
        orgId: res.data.brandId,
        role: decoded?.role ?? role,
      });
      setOpen(false);
      navigate('/dashboard');
      toast.success(`Switched to ${brand.name}`);
    } catch {
      toast.error('Could not switch website');
    } finally {
      setSwitchingId(null);
    }
  }

  async function createWebsite() {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await api.post<BrandSummary>('/account/brands', { name });
      setBrands([...brands, res.data]);
      setNewName('');
      setAdding(false);
      await switchTo(res.data);
      toast.success(`Website "${res.data.name}" created`);
    } catch {
      toast.error('Could not create website');
    } finally {
      setCreating(false);
    }
  }

  // ── Collapsed: just the Pulse mark ──
  if (!expanded) {
    return (
      <Tooltip content={accountName ?? 'Workspace'} side="right">
        <button
          type="button"
          onClick={() => navigate('/dashboard')}
          style={{ display: 'flex', justifyContent: 'center', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          aria-label={accountName ?? 'Workspace'}
        >
          <BrandMark />
        </button>
      </Tooltip>
    );
  }

  const dropdownId = 'workspace-switcher-menu';
  const topName = accountName ?? currentBrand?.name ?? 'Workspace';
  // Only show the website name on the trigger when it differs from the
  // umbrella org name (multi-website accounts); otherwise surface the plan.
  const showWebsiteName = !!currentBrand && currentBrand.name !== (accountName ?? '');

  return (
    <div style={{ position: 'relative' }}>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((p) => !p)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={dropdownId}
        aria-label={`${topName} — active website ${currentBrand?.name ?? 'none'}. Switch website.`}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '6px 8px',
          borderRadius: 10,
          background: open ? 'var(--side-bg-2)' : 'transparent',
          border: '1px solid transparent',
          color: 'var(--side-text-strong)',
          cursor: 'pointer',
          transition: 'background 120ms var(--ease-out)',
          fontFamily: 'inherit',
        }}
        className="hover:bg-[var(--side-bg-2)]"
      >
        {/* Pulse logo */}
        <BrandMark />
        <div style={{ minWidth: 0, flex: 1, textAlign: 'left' }}>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--side-text-strong)', letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {topName}
          </div>
          <div style={{ marginTop: 1, display: 'flex', alignItems: 'center', gap: 4, color: 'var(--side-text)' }}>
            {showWebsiteName ? (
              <>
                <Globe size={11} style={{ flexShrink: 0, opacity: 0.8 }} />
                <span style={{ fontSize: 11, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {currentBrand?.name}
                </span>
              </>
            ) : (
              <span
                style={{
                  fontSize: 9.5,
                  fontWeight: 600,
                  letterSpacing: '0.02em',
                  textTransform: 'uppercase',
                  color: 'var(--brand-hover)',
                  background: 'var(--brand-subtle)',
                  border: '1px solid var(--brand-muted)',
                  padding: '1px 6px',
                  borderRadius: 9999,
                  lineHeight: 1.5,
                }}
              >
                {planLabel(currentBrand?.planId)}
              </span>
            )}
          </div>
        </div>
        <ChevronsUpDown size={14} style={{ opacity: 0.5, flexShrink: 0 }} aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={menuRef}
          id={dropdownId}
          role="menu"
          aria-label="Switch website"
          style={{
            position: 'absolute',
            left: 0,
            top: '100%',
            marginTop: 6,
            width: '100%',
            zIndex: 60,
            background: 'var(--surface-overlay, var(--surface-card))',
            border: '1px solid var(--border-default)',
            borderRadius: 12,
            boxShadow: 'var(--shadow-lg)',
            overflow: 'hidden',
          }}
        >
          <div style={{ padding: '8px 12px 6px', fontSize: 10, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--text-tertiary)', fontWeight: 600 }}>
            Websites
          </div>
          <div style={{ maxHeight: 260, overflowY: 'auto', padding: '0 6px' }}>
            {brands.map((brand) => {
              const active = brand.id === orgId;
              return (
                <button
                  key={brand.id}
                  type="button"
                  role="menuitemradio"
                  aria-checked={active}
                  onClick={() => void switchTo(brand)}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 8px',
                    borderRadius: 8,
                    border: 'none',
                    background: active ? 'var(--brand-subtle)' : 'transparent',
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                  className="hover:bg-[var(--bg-subtle)]"
                >
                  <WebsiteMark brand={brand} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {brand.name}
                    </div>
                    <div style={{ fontSize: 10.5, color: 'var(--text-tertiary)', marginTop: 1 }}>
                      {planLabel(brand.planId)}
                    </div>
                  </div>
                  {switchingId === brand.id ? (
                    <Loader2 size={14} className="animate-spin" style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
                  ) : active ? (
                    <Check size={15} style={{ color: 'var(--brand-default)', flexShrink: 0 }} />
                  ) : null}
                </button>
              );
            })}
          </div>

          <div style={{ borderTop: '1px solid var(--border-subtle)', padding: 6 }}>
            {adding ? (
              creating ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', fontSize: 12, color: 'var(--text-tertiary)' }} role="status" aria-live="polite">
                  <Loader2 size={13} className="animate-spin" /> Creating website…
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <label htmlFor="new-website-name" className="sr-only">New website name</label>
                  <input
                    id="new-website-name"
                    autoFocus
                    value={newName}
                    onChange={(e) => setNewName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') void createWebsite();
                      if (e.key === 'Escape') { setAdding(false); setNewName(''); }
                    }}
                    placeholder="Website name…"
                    style={{
                      flex: 1, height: 32, borderRadius: 8, padding: '0 10px', fontSize: 12.5,
                      border: '1px solid var(--border-default)', background: 'var(--bg-base)', color: 'var(--text-primary)', outline: 'none', fontFamily: 'inherit',
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => void createWebsite()}
                    disabled={!newName.trim()}
                    aria-label="Create website"
                    style={{
                      width: 32, height: 32, borderRadius: 8, border: 'none', display: 'grid', placeItems: 'center',
                      background: 'var(--brand-default)', color: 'white', cursor: 'pointer', flexShrink: 0,
                      opacity: newName.trim() ? 1 : 0.4,
                    }}
                  >
                    <Check size={15} />
                  </button>
                </div>
              )
            ) : (
              <button
                type="button"
                onClick={() => setAdding(true)}
                style={{
                  width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '8px 8px', borderRadius: 8,
                  border: 'none', background: 'transparent', color: 'var(--brand-hover)', cursor: 'pointer', fontFamily: 'inherit',
                  fontSize: 13, fontWeight: 500, textAlign: 'left',
                }}
                className="hover:bg-[var(--bg-subtle)]"
              >
                <span style={{ width: 22, height: 22, borderRadius: 6, border: '1px dashed var(--border-strong)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
                  <Plus size={13} />
                </span>
                Add website
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Avatar ────────────────────────────────────────────────────────

function UserAvatar({ name }: { name: string }) {
  const initials = name
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0])
    .join('')
    .toUpperCase();
  return (
    <div style={{ position: 'relative', flexShrink: 0 }} aria-hidden="true">
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: 9999,
          background: 'linear-gradient(135deg, oklch(62% 0.18 270), oklch(44% 0.22 270))',
          color: 'white',
          display: 'grid',
          placeItems: 'center',
          fontSize: 11,
          fontWeight: 600,
        }}
      >
        {initials || '?'}
      </div>
      {/* Online status indicator */}
      <span
        style={{
          position: 'absolute',
          right: -1,
          bottom: -1,
          width: 9,
          height: 9,
          borderRadius: 9999,
          background: 'var(--success-default)',
          border: '2px solid var(--side-bg)',
        }}
      />
    </div>
  );
}

// ── Sidebar ───────────────────────────────────────────────────────

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  onOpenShortcuts: () => void;
}

export function Sidebar({ collapsed, onToggle, onOpenShortcuts: _onOpenShortcuts }: SidebarProps) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const clearAuth = useAuthStore((s) => s.clearAuth);
  const { resolvedTheme, setTheme } = useTheme();
  const isDark = resolvedTheme === 'dark';

  const expanded = !collapsed;
  const [subOpen, setSubOpen] = React.useState<Set<string>>(() => new Set());

  // Sync CSS var with expansion state
  React.useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-current-w',
      expanded ? 'var(--sidebar-width)' : 'var(--sidebar-collapsed)',
    );
  }, [expanded]);

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.email || 'User';

  const toggleSub = (id: string) => {
    const next = new Set(subOpen);
    next.has(id) ? next.delete(id) : next.add(id);
    setSubOpen(next);
  };

  const sideStyle: React.CSSProperties = {
    background: 'var(--side-bg)',
    borderRight: '1px solid var(--side-line, var(--border-subtle))',
    display: 'flex',
    flexDirection: 'column',
    position: 'sticky',
    top: 0,
    height: '100vh',
    width: expanded ? 'var(--sidebar-width)' : 'var(--sidebar-collapsed)',
    minWidth: expanded ? 'var(--sidebar-width)' : 'var(--sidebar-collapsed)',
    transition: 'width 160ms ease, min-width 160ms ease',
    overflow: 'hidden',
    zIndex: 30,
    flexShrink: 0,
  };

  return (
    <aside aria-label="Application sidebar" aria-expanded={expanded} data-sidebar="pulse" style={sideStyle}>
      {/* ── Workspace switcher ─────────────────────────────────── */}
      <div
        style={{
          padding: expanded ? '14px 12px 12px' : '14px 12px',
          borderBottom: '1px solid var(--side-line, var(--border-subtle))',
          flexShrink: 0,
        }}
      >
        <WorkspaceSwitcher expanded={expanded} />
      </div>

      {/* ── Navigation ────────────────────────────────────────── */}
      <nav
        aria-label="Main navigation"
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '6px 0 8px',
        }}
      >
        {NAV.map((g, gi) => {
          const GroupIcon = g.icon;
          return (
          <div key={gi} style={{ padding: '2px 10px' }}>
            {expanded ? (
              <div className="pulse-nav-group-label">
                <GroupIcon size={13} aria-hidden="true" />
                {g.group}
              </div>
            ) : (
              gi > 0 && (
                <div
                  aria-hidden="true"
                  style={{ height: 1, background: 'var(--side-line)', margin: '8px 6px 6px', opacity: 0.6 }}
                />
              )
            )}

            {g.items.map((item) => {
              const Icon = item.icon;
              const isOpen = subOpen.has(item.id);

              const navLinkEl = (
                <NavLink
                  key={item.id}
                  to={item.to}
                  aria-label={!expanded ? item.label : undefined}
                  end={item.id === 'dashboard'}
                  onClick={(e) => {
                    if (item.children) {
                      e.preventDefault();
                      toggleSub(item.id);
                    }
                  }}
                  className={({ isActive }) =>
                    cn('pulse-nav-item', (isActive || (item.children && item.children.some((c) => location.pathname.startsWith(c.to)))) ? 'active' : '')
                  }
                  style={{
                    justifyContent: expanded ? 'flex-start' : 'center',
                    padding: expanded ? '10px 10px' : '11px',
                  }}
                >
                  <Icon size={18} style={{ flexShrink: 0 }} />
                  {expanded && (
                    <>
                      <span style={{ flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.label}</span>
                      {item.badge && (
                        <span
                          style={{
                            fontSize: 10,
                            background: 'var(--brand-default)',
                            color: 'white',
                            padding: '1px 6px',
                            borderRadius: 9999,
                            fontWeight: 500,
                          }}
                        >
                          {item.badge}
                        </span>
                      )}
                      {item.children && (
                        <ChevronDown
                          size={11}
                          style={{
                            transform: isOpen ? 'rotate(0)' : 'rotate(-90deg)',
                            transition: 'transform 120ms',
                            opacity: 0.6,
                            flexShrink: 0,
                          }}
                        />
                      )}
                    </>
                  )}
                </NavLink>
              );

              return (
                <React.Fragment key={item.id}>
                  {!expanded ? (
                    <Tooltip content={item.label} side="right">
                      <div>{navLinkEl}</div>
                    </Tooltip>
                  ) : (
                    navLinkEl
                  )}

                  {expanded && item.children && isOpen && (
                    <div style={{ marginTop: 2, marginBottom: 6 }}>
                      {item.children.map((child) => (
                        <NavLink
                          key={child.id}
                          to={child.to}
                          className={({ isActive }) =>
                            cn('pulse-nav-sub', isActive ? 'active' : '')
                          }
                        >
                          <span>{child.label}</span>
                          {child.pill != null && (
                            <span style={{ fontSize: 10, color: 'var(--text-disabled)', fontFamily: 'var(--font-mono)', marginLeft: 'auto' }}>
                              {child.pill}
                            </span>
                          )}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
          );
        })}
      </nav>

      {/* ── Footer ────────────────────────────────────────────── */}
      <div
        style={{
          padding: 10,
          borderTop: '1px solid var(--side-line, var(--border-subtle))',
          display: 'flex',
          flexDirection: 'column',
          gap: 4,
          flexShrink: 0,
        }}
      >
        {/* Settings */}
        {expanded ? (
          <NavLink
            to="/settings"
            className={({ isActive }) => cn('pulse-nav-item', isActive ? 'active' : '')}
          >
            <Settings size={17} />
            <span>Settings</span>
          </NavLink>
        ) : (
          <Tooltip content="Settings" side="right">
            <NavLink
              to="/settings"
              className={({ isActive }) => cn('pulse-nav-item', isActive ? 'active' : '')}
              style={{ justifyContent: 'center', padding: 10 }}
            >
              <Settings size={17} />
            </NavLink>
          </Tooltip>
        )}

        {/* Help */}
        {expanded ? (
          <button
            type="button"
            className="pulse-nav-item"
            onClick={_onOpenShortcuts}
          >
            <CircleHelp size={17} />
            <span>Help &amp; docs</span>
          </button>
        ) : (
          <Tooltip content="Help & shortcuts" side="right">
            <button
              type="button"
              className="pulse-nav-item"
              style={{ justifyContent: 'center', padding: 10 }}
              onClick={_onOpenShortcuts}
            >
              <CircleHelp size={17} />
            </button>
          </Tooltip>
        )}

        {/* Collapse toggle */}
        <button
          type="button"
          onClick={onToggle}
          aria-label={expanded ? 'Collapse sidebar' : 'Expand sidebar'}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: expanded ? 'flex-start' : 'center',
            gap: 10,
            width: '100%',
            padding: 8,
            borderRadius: 8,
            border: 'none',
            background: 'transparent',
            color: 'var(--text-tertiary)',
            fontSize: 12,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
          className="hover:bg-[var(--bg-subtle)] hover:text-[var(--text-primary)]"
        >
          {expanded ? <ChevronLeft size={14} /> : <ChevronRight size={14} />}
          {expanded && <span>Collapse</span>}
        </button>

        {/* User chip */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: 8,
            borderRadius: 8,
            cursor: 'pointer',
            justifyContent: expanded ? 'flex-start' : 'center',
          }}
          className="hover:bg-[var(--bg-subtle)]"
          title={`${displayName} · ${user?.email}`}
        >
          <UserAvatar name={displayName} />
          {expanded && (
            <>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: 12.5, color: 'var(--text-primary)', fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{displayName}</div>
                <div style={{ fontSize: 11, color: 'var(--text-tertiary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{user?.email}</div>
              </div>
              <button
                type="button"
                title={isDark ? 'Light mode' : 'Dark mode'}
                onClick={() => setTheme(isDark ? 'light' : 'dark')}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 2, flexShrink: 0 }}
              >
                {isDark ? <Sun size={14} /> : <Moon size={14} />}
              </button>
              <button
                type="button"
                title="Sign out"
                onClick={() => { clearAuth(); navigate('/login'); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-tertiary)', padding: 2, flexShrink: 0 }}
              >
                <LogOut size={14} />
              </button>
            </>
          )}
        </div>

        {/* ⌘K hint */}
        {expanded && (
          <p style={{ padding: '0 8px', fontSize: 11, color: 'var(--text-disabled)', margin: 0 }}>
            <kbd style={{ fontFamily: 'var(--font-mono)' }}>⌘K</kbd> to search
          </p>
        )}
      </div>
    </aside>
  );
}
